import {
  AfterViewInit,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { ActiveElement, Chart, ChartEvent, registerables } from "chart.js";

export type FirmwareFilterMode = "all" | "latest" | "outdated";

export interface PcdConfigRow {
  config: string;
  devices: number;
  percentage: string;
  lastUsed: string;
}

interface FirmwareSliceDef {
  id: string;
  count: number;
  color: string;
  isLatest: boolean;
}

interface SubcategoryDef {
  code: string;
  count: number;
  firmware: FirmwareSliceDef[];
  latestBar: number;
  outdatedBar: number;
  configsByFirmware: Record<string, PcdConfigRow[]>;
}

interface ProductFamilyDef {
  id: string;
  label: string;
  deviceCount: number;
  subcategories: SubcategoryDef[];
}

@Component({
  selector: "app-product-code-distribution",
  templateUrl: "./product-code-distribution.component.html",
  styleUrls: ["./product-code-distribution.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductCodeDistributionComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly fleetOptions = [
    "Metro Fleet",
    "Regional Fleet",
    "National Fleet",
    "Last Mile Delivery",
  ];

  fleetControl = new UntypedFormControl("Metro Fleet");

  readonly products: ProductFamilyDef[] = [
    {
      id: "dcm970",
      label: "DCM970",
      deviceCount: 1500,
      subcategories: [
        {
          code: "DCM970-21ZB2",
          count: 420,
          latestBar: 78,
          outdatedBar: 22,
          firmware: [
            { id: "D001A549", count: 155, color: "#4E8DDE", isLatest: true },
            { id: "D0017739", count: 93, color: "#6366F1", isLatest: true },
            { id: "D0021954", count: 80, color: "#10B77F", isLatest: false },
            { id: "D0028833", count: 47, color: "#A78BFA", isLatest: false },
            { id: "Others", count: 45, color: "#94A3B8", isLatest: false },
          ],
          configsByFirmware: {
            D001A549: [
              {
                config: "FL. 901",
                devices: 320,
                percentage: "32%",
                lastUsed: "Today",
              },
              {
                config: "SL. 4102",
                devices: 130,
                percentage: "13%",
                lastUsed: "1 week ago",
              },
              {
                config: "FL. 3101",
                devices: 250,
                percentage: "25%",
                lastUsed: "Today",
              },
              {
                config: "CZ. 601",
                devices: 110,
                percentage: "11%",
                lastUsed: "1 month ago",
              },
              {
                config: "FL. 900",
                devices: 60,
                percentage: "6%",
                lastUsed: "1 month ago",
              },
              {
                config: "FL. 3100",
                devices: 40,
                percentage: "4%",
                lastUsed: "1 month ago",
              },
              {
                config: "SL. 4101",
                devices: 30,
                percentage: "3%",
                lastUsed: "2 months ago",
              },
              {
                config: "CZ. 600",
                devices: 30,
                percentage: "3%",
                lastUsed: "5 months ago",
              },
              {
                config: "SL. 4100",
                devices: 20,
                percentage: "2%",
                lastUsed: "1 year ago",
              },
              {
                config: "Others",
                devices: 10,
                percentage: "1%",
                lastUsed: "1 year ago",
              },
            ],
            D0017739: [
              {
                config: "FL. 210",
                devices: 40,
                percentage: "43%",
                lastUsed: "Today",
              },
              {
                config: "SL. 1200",
                devices: 35,
                percentage: "38%",
                lastUsed: "3 days ago",
              },
              {
                config: "CZ. 500",
                devices: 18,
                percentage: "19%",
                lastUsed: "2 weeks ago",
              },
            ],
            D0021954: [
              {
                config: "FL. 880",
                devices: 50,
                percentage: "63%",
                lastUsed: "Today",
              },
              {
                config: "SL. 990",
                devices: 30,
                percentage: "37%",
                lastUsed: "1 month ago",
              },
            ],
            D0028833: [
              {
                config: "FL. 701",
                devices: 25,
                percentage: "53%",
                lastUsed: "Today",
              },
              {
                config: "SL. 702",
                devices: 22,
                percentage: "47%",
                lastUsed: "5 days ago",
              },
            ],
            Others: [
              {
                config: "Mixed",
                devices: 45,
                percentage: "100%",
                lastUsed: "—",
              },
            ],
          },
        },
        {
          code: "DCM970-21ZB70",
          count: 310,
          latestBar: 65,
          outdatedBar: 35,
          firmware: [
            { id: "D001A549", count: 120, color: "#4E8DDE", isLatest: true },
            { id: "D0019900", count: 95, color: "#6366F1", isLatest: true },
            { id: "D0022001", count: 55, color: "#0CC0DF", isLatest: false },
            { id: "Others", count: 40, color: "#94A3B8", isLatest: false },
          ],
          configsByFirmware: {
            D001A549: [
              {
                config: "FL. 901",
                devices: 200,
                percentage: "65%",
                lastUsed: "Today",
              },
              {
                config: "SL. 4102",
                devices: 110,
                percentage: "35%",
                lastUsed: "2 weeks ago",
              },
            ],
            D0019900: [
              {
                config: "FL. 330",
                devices: 60,
                percentage: "63%",
                lastUsed: "Today",
              },
              {
                config: "CZ. 110",
                devices: 35,
                percentage: "37%",
                lastUsed: "1 month ago",
              },
            ],
            D0022001: [
              {
                config: "SL. 770",
                devices: 55,
                percentage: "100%",
                lastUsed: "3 days ago",
              },
            ],
            Others: [
              {
                config: "Legacy",
                devices: 40,
                percentage: "100%",
                lastUsed: "6 months ago",
              },
            ],
          },
        },
        {
          code: "DCM970-44ZB",
          count: 180,
          latestBar: 82,
          outdatedBar: 18,
          firmware: [
            { id: "D001A549", count: 67, color: "#4E8DDE", isLatest: true },
            { id: "D0028833", count: 40, color: "#A78BFA", isLatest: false },
            { id: "D0031002", count: 38, color: "#10B77F", isLatest: true },
            { id: "Others", count: 35, color: "#94A3B8", isLatest: false },
          ],
          configsByFirmware: {
            D001A549: [
              {
                config: "FL. 901",
                devices: 67,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            D0028833: [
              {
                config: "SL. 702",
                devices: 40,
                percentage: "100%",
                lastUsed: "1 week ago",
              },
            ],
            D0031002: [
              {
                config: "FL. 500",
                devices: 38,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            Others: [
              {
                config: "Various",
                devices: 35,
                percentage: "100%",
                lastUsed: "—",
              },
            ],
          },
        },
        {
          code: "DCM970-12AA",
          count: 170,
          latestBar: 50,
          outdatedBar: 50,
          firmware: [
            { id: "D0040001", count: 90, color: "#4E8DDE", isLatest: true },
            { id: "D0040002", count: 80, color: "#F59F0A", isLatest: false },
          ],
          configsByFirmware: {
            D0040001: [
              {
                config: "FL. 100",
                devices: 90,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            D0040002: [
              {
                config: "SL. 200",
                devices: 80,
                percentage: "100%",
                lastUsed: "2 months ago",
              },
            ],
          },
        },
        {
          code: "DCM970-08Q",
          count: 200,
          latestBar: 70,
          outdatedBar: 30,
          firmware: [
            { id: "D001A549", count: 100, color: "#4E8DDE", isLatest: true },
            { id: "D0055005", count: 100, color: "#EC4899", isLatest: false },
          ],
          configsByFirmware: {
            D001A549: [
              {
                config: "FL. 901",
                devices: 100,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            D0055005: [
              {
                config: "SL. 600",
                devices: 100,
                percentage: "100%",
                lastUsed: "3 weeks ago",
              },
            ],
          },
        },
        {
          code: "DCM970-99X",
          count: 80,
          latestBar: 40,
          outdatedBar: 60,
          firmware: [
            { id: "D0099009", count: 80, color: "#647496", isLatest: false },
          ],
          configsByFirmware: {
            D0099009: [
              {
                config: "FL. 009",
                devices: 80,
                percentage: "100%",
                lastUsed: "1 year ago",
              },
            ],
          },
        },
        {
          code: "DCM970-77M",
          count: 60,
          latestBar: 100,
          outdatedBar: 0,
          firmware: [
            { id: "D001A549", count: 60, color: "#4E8DDE", isLatest: true },
          ],
          configsByFirmware: {
            D001A549: [
              {
                config: "FL. 901",
                devices: 60,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
          },
        },
        {
          code: "DCM970-55K",
          count: 35,
          latestBar: 20,
          outdatedBar: 80,
          firmware: [
            { id: "D0077007", count: 20, color: "#10B77F", isLatest: true },
            { id: "D0077008", count: 15, color: "#94A3B8", isLatest: false },
          ],
          configsByFirmware: {
            D0077007: [
              {
                config: "SL. 055",
                devices: 20,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            D0077008: [
              {
                config: "FL. 055",
                devices: 15,
                percentage: "100%",
                lastUsed: "4 months ago",
              },
            ],
          },
        },
      ],
    },
    {
      id: "dl972",
      label: "DL972",
      deviceCount: 2000,
      subcategories: [
        {
          code: "DL972-10A",
          count: 520,
          latestBar: 88,
          outdatedBar: 12,
          firmware: [
            { id: "D0100101", count: 260, color: "#4E8DDE", isLatest: true },
            { id: "D0100102", count: 160, color: "#6366F1", isLatest: true },
            { id: "D0100103", count: 100, color: "#10B77F", isLatest: false },
          ],
          configsByFirmware: {
            D0100101: [
              {
                config: "FL. A01",
                devices: 260,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            D0100102: [
              {
                config: "SL. A02",
                devices: 160,
                percentage: "100%",
                lastUsed: "2 days ago",
              },
            ],
            D0100103: [
              {
                config: "CZ. A03",
                devices: 100,
                percentage: "100%",
                lastUsed: "1 month ago",
              },
            ],
          },
        },
        {
          code: "DL972-20B",
          count: 480,
          latestBar: 60,
          outdatedBar: 40,
          firmware: [
            { id: "D0200201", count: 200, color: "#4E8DDE", isLatest: true },
            { id: "D0200202", count: 180, color: "#F59F0A", isLatest: false },
            { id: "Others", count: 100, color: "#94A3B8", isLatest: false },
          ],
          configsByFirmware: {
            D0200201: [
              {
                config: "FL. B01",
                devices: 200,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            D0200202: [
              {
                config: "SL. B02",
                devices: 180,
                percentage: "100%",
                lastUsed: "3 weeks ago",
              },
            ],
            Others: [
              {
                config: "Mixed B",
                devices: 100,
                percentage: "100%",
                lastUsed: "—",
              },
            ],
          },
        },
        {
          code: "DL972-30C",
          count: 410,
          latestBar: 72,
          outdatedBar: 28,
          firmware: [
            { id: "D0300301", count: 410, color: "#0CC0DF", isLatest: true },
          ],
          configsByFirmware: {
            D0300301: [
              {
                config: "FL. C99",
                devices: 410,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
          },
        },
        {
          code: "DL972-40D",
          count: 310,
          latestBar: 45,
          outdatedBar: 55,
          firmware: [
            { id: "D0400401", count: 150, color: "#6366F1", isLatest: true },
            { id: "D0400402", count: 160, color: "#EC4899", isLatest: false },
          ],
          configsByFirmware: {
            D0400401: [
              {
                config: "SL. D1",
                devices: 150,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            D0400402: [
              {
                config: "FL. D2",
                devices: 160,
                percentage: "100%",
                lastUsed: "2 months ago",
              },
            ],
          },
        },
        {
          code: "DL972-50E",
          count: 280,
          latestBar: 90,
          outdatedBar: 10,
          firmware: [
            { id: "D0500501", count: 280, color: "#4E8DDE", isLatest: true },
          ],
          configsByFirmware: {
            D0500501: [
              {
                config: "FL. E77",
                devices: 280,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
          },
        },
      ],
    },
    {
      id: "dl974",
      label: "DL974",
      deviceCount: 1200,
      subcategories: [
        {
          code: "DL974-PRO",
          count: 400,
          latestBar: 75,
          outdatedBar: 25,
          firmware: [
            { id: "D1111111", count: 220, color: "#4E8DDE", isLatest: true },
            { id: "D1111112", count: 120, color: "#10B77F", isLatest: false },
            { id: "Others", count: 60, color: "#94A3B8", isLatest: false },
          ],
          configsByFirmware: {
            D1111111: [
              {
                config: "FL. PRO1",
                devices: 220,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            D1111112: [
              {
                config: "SL. PRO2",
                devices: 120,
                percentage: "100%",
                lastUsed: "1 week ago",
              },
            ],
            Others: [
              {
                config: "Legacy PRO",
                devices: 60,
                percentage: "100%",
                lastUsed: "6 months ago",
              },
            ],
          },
        },
        {
          code: "DL974-LITE",
          count: 360,
          latestBar: 92,
          outdatedBar: 8,
          firmware: [
            { id: "D2222222", count: 360, color: "#6366F1", isLatest: true },
          ],
          configsByFirmware: {
            D2222222: [
              {
                config: "FL. L01",
                devices: 360,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
          },
        },
        {
          code: "DL974-MAX",
          count: 280,
          latestBar: 55,
          outdatedBar: 45,
          firmware: [
            { id: "D3333333", count: 140, color: "#4E8DDE", isLatest: true },
            { id: "D3333334", count: 140, color: "#F59F0A", isLatest: false },
          ],
          configsByFirmware: {
            D3333333: [
              {
                config: "FL. M1",
                devices: 140,
                percentage: "100%",
                lastUsed: "Today",
              },
            ],
            D3333334: [
              {
                config: "SL. M2",
                devices: 140,
                percentage: "100%",
                lastUsed: "1 month ago",
              },
            ],
          },
        },
        {
          code: "DL974-EDGE",
          count: 160,
          latestBar: 30,
          outdatedBar: 70,
          firmware: [
            { id: "D4444444", count: 160, color: "#94A3B8", isLatest: false },
          ],
          configsByFirmware: {
            D4444444: [
              {
                config: "FL. EDGE",
                devices: 160,
                percentage: "100%",
                lastUsed: "3 months ago",
              },
            ],
          },
        },
      ],
    },
  ];

  selectedProductIndex = 0;
  selectedSubIndex = 0;
  selectedFirmwareId: string | null = null;
  firmwareFilter: FirmwareFilterMode = "all";

  productCodeSearch = "";
  configSearch = "";

  configPage = 1;
  readonly configPageSize = 10;

  private donutChart: any = null;
  private readonly canvasId = "productCodeDistributionDonut";

  constructor(private readonly ngZone: NgZone) {}

  ngOnInit(): void {
    Chart.register(...registerables);
    this.syncFirmwareSelection();
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.renderDonut());
  }

  ngOnDestroy(): void {
    this.destroyDonut();
  }

  get activeProduct(): ProductFamilyDef {
    return this.products[this.selectedProductIndex] ?? this.products[0];
  }

  get activeSub(): SubcategoryDef {
    const subs = this.activeProduct.subcategories;
    return subs[this.selectedSubIndex] ?? subs[0];
  }

  get filteredSubcategories(): SubcategoryDef[] {
    const q = this.productCodeSearch.trim().toLowerCase();
    const subs = this.activeProduct.subcategories;
    if (!q) {
      return subs;
    }
    return subs.filter((s) => s.code.toLowerCase().includes(q));
  }

  get maxSubCount(): number {
    const subs = this.filteredSubcategories;
    if (!subs.length) {
      return 1;
    }
    return Math.max(...subs.map((s) => s.count), 1);
  }

  get filteredFirmware(): FirmwareSliceDef[] {
    const sub = this.activeSub;
    switch (this.firmwareFilter) {
      case "latest":
        return sub.firmware.filter((f) => f.isLatest);
      case "outdated":
        return sub.firmware.filter((f) => !f.isLatest);
      default:
        return [...sub.firmware];
    }
  }

  get firmwareLegendRows(): { slice: FirmwareSliceDef; pct: number }[] {
    const rows = this.filteredFirmware;
    const total = rows.reduce((a, b) => a + b.count, 0) || 1;
    return rows.map((slice) => ({
      slice,
      pct: Math.round((slice.count / total) * 100),
    }));
  }

  get selectedFirmwareSlice(): FirmwareSliceDef | null {
    const rows = this.filteredFirmware;
    if (!rows.length) {
      return null;
    }
    const id = this.selectedFirmwareId;
    const found = id ? rows.find((r) => r.id === id) : null;
    return found ?? rows[0];
  }

  get configRowsAll(): PcdConfigRow[] {
    const fw = this.selectedFirmwareSlice;
    if (!fw) {
      return [];
    }
    const rows = this.activeSub.configsByFirmware[fw.id];
    return rows ? [...rows] : [];
  }

  get configRowsFiltered(): PcdConfigRow[] {
    const q = this.configSearch.trim().toLowerCase();
    const all = this.configRowsAll;
    if (!q) {
      return all;
    }
    return all.filter(
      (r) =>
        r.config.toLowerCase().includes(q) ||
        r.lastUsed.toLowerCase().includes(q)
    );
  }

  get configPageCount(): number {
    return Math.max(
      1,
      Math.ceil(this.configRowsFiltered.length / this.configPageSize)
    );
  }

  get configPageNumbers(): number[] {
    return Array.from({ length: this.configPageCount }, (_, i) => i + 1);
  }

  get configRowsPage(): PcdConfigRow[] {
    const start = (this.configPage - 1) * this.configPageSize;
    return this.configRowsFiltered.slice(
      start,
      start + this.configPageSize
    );
  }

  get configRangeLabel(): string {
    const total = this.configRowsFiltered.length;
    if (total === 0) {
      return "0 of 0";
    }
    const start = (this.configPage - 1) * this.configPageSize + 1;
    const end = Math.min(this.configPage * this.configPageSize, total);
    return `${start}-${end} of ${total}`;
  }

  get maxFirmwareBar(): number {
    const rows = this.filteredFirmware;
    if (!rows.length) {
      return 1;
    }
    return Math.max(...rows.map((r) => r.count), 1);
  }

  selectProduct(index: number, event?: Event): void {
    event?.preventDefault();
    if (index < 0 || index >= this.products.length) {
      return;
    }
    if (index === this.selectedProductIndex) {
      return;
    }
    this.selectedProductIndex = index;
    this.selectedSubIndex = 0;
    this.firmwareFilter = "all";
    this.syncFirmwareSelection();
    this.resetConfigUi();
    this.renderDonut();
  }

  selectSubcategory(sub: SubcategoryDef, event?: Event): void {
    event?.preventDefault();
    const idx = this.activeProduct.subcategories.indexOf(sub);
    if (idx < 0 || idx === this.selectedSubIndex) {
      return;
    }
    this.selectedSubIndex = idx;
    this.syncFirmwareSelection();
    this.resetConfigUi();
    this.renderDonut();
  }

  selectFirmwareId(id: string): void {
    const rows = this.filteredFirmware;
    if (!rows.some((r) => r.id === id)) {
      return;
    }
    this.selectedFirmwareId = id;
    this.resetConfigUi();
    this.highlightDonutSelection();
  }

  onProductSearchChange(): void {
    const vis = this.filteredSubcategories;
    if (!vis.length) {
      return;
    }
    if (!vis.includes(this.activeSub)) {
      this.selectedSubIndex = this.activeProduct.subcategories.indexOf(vis[0]);
      this.syncFirmwareSelection();
      this.resetConfigUi();
      this.renderDonut();
    }
  }

  onConfigSearchChange(): void {
    this.configPage = 1;
  }

  setFirmwareFilter(mode: FirmwareFilterMode): void {
    if (this.firmwareFilter === mode) {
      return;
    }
    this.firmwareFilter = mode;
    this.syncFirmwareSelection();
    this.resetConfigUi();
    this.renderDonut();
  }

  onLegendRowClick(row: FirmwareSliceDef, event?: Event): void {
    event?.preventDefault();
    this.selectFirmwareId(row.id);
  }

  onConfigPage(delta: number): void {
    const next = this.configPage + delta;
    if (next >= 1 && next <= this.configPageCount) {
      this.configPage = next;
    }
  }

  goConfigPage(page: number): void {
    if (page >= 1 && page <= this.configPageCount) {
      this.configPage = page;
    }
  }

  isProductSelected(index: number): boolean {
    return this.selectedProductIndex === index;
  }

  isSubSelected(sub: SubcategoryDef): boolean {
    return this.activeSub === sub;
  }

  isFirmwareSelected(slice: FirmwareSliceDef): boolean {
    return this.selectedFirmwareSlice?.id === slice.id;
  }

  trackSub(_i: number, sub: SubcategoryDef): string {
    return sub.code;
  }

  trackFw(_i: number, row: { slice: FirmwareSliceDef }): string {
    return row.slice.id;
  }

  trackCfg(_i: number, row: PcdConfigRow): string {
    return row.config + row.lastUsed;
  }

  onProductKeydown(index: number, ev: KeyboardEvent): void {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      this.selectProduct(index);
    }
  }

  private resetConfigUi(): void {
    this.configPage = 1;
  }

  private syncFirmwareSelection(): void {
    const rows = this.filteredFirmware;
    if (!rows.length) {
      this.selectedFirmwareId = null;
      return;
    }
    if (
      !this.selectedFirmwareId ||
      !rows.some((r) => r.id === this.selectedFirmwareId)
    ) {
      this.selectedFirmwareId = rows[0].id;
    }
  }

  private destroyDonut(): void {
    if (this.donutChart) {
      this.donutChart.destroy();
      this.donutChart = null;
    }
  }

  private renderDonut(): void {
    this.destroyDonut();
    const canvas = document.getElementById(
      this.canvasId
    ) as HTMLCanvasElement | null;
    if (!canvas) {
      return;
    }
    const rows = this.filteredFirmware;
    if (!rows.length) {
      return;
    }
    const data = rows.map((r) => r.count);
    const colors = rows.map((r) => r.color);
    const labels = rows.map((r) => r.id);

    this.donutChart = new Chart(canvas, {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: colors,
            borderColor: "#fff",
            borderWidth: 2,
            hoverOffset: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        layout: { padding: 4 },
        onClick: (ev: ChartEvent, elements: ActiveElement[]) =>
          this.onDonutClick(elements),
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (item) => {
                const values = item.chart.data.datasets[0].data as number[];
                const sum = values.reduce((a, b) => a + b, 0);
                const v = Number(item.raw);
                const pct = sum ? Math.round((v / sum) * 100) : 0;
                return `${item.label ?? ""}: ${v} (${pct}%)`;
              },
            },
          },
        },
      },
    });
    this.highlightDonutSelection();
  }

  private onDonutClick(elements: ActiveElement[]): void {
    if (!elements?.length) {
      return;
    }
    const idx = elements[0].index;
    const row = this.filteredFirmware[idx];
    if (!row) {
      return;
    }
    this.ngZone.run(() => this.selectFirmwareId(row.id));
  }

  private highlightDonutSelection(): void {
    if (!this.donutChart) {
      return;
    }
    const rows = this.filteredFirmware;
    const sel = this.selectedFirmwareId;
    const ds = this.donutChart.data.datasets[0];
    ds.borderWidth = rows.map((r) => (r.id === sel ? 5 : 2));
    ds.borderColor = rows.map((r) =>
      r.id === sel ? "#5f9ce9" : "#ffffff"
    );
    this.donutChart.update();
  }
}
